import {
  ACCOUNT_NAV_LABELS,
  ACCOUNT_OWNER_ONLY,
  ACCOUNT_PAGE_IDS,
  ACCOUNT_PATHS,
  accountPageInSprint,
  type AccountPageId,
} from '@/lib/accountPaths';
import type { AccountRole } from './accountFixture';
import { showsTeamPage } from './accountModel';

/**
 * AR-380 — the settings search at the top of the Account column.
 *
 * The index is DERIVED from the pages' own field labels, not typed twice: each
 * entry names the page it is on and the card that holds it, so a result opens
 * the card holding the field. A row that is not on a page cannot be found.
 *
 * The prototype derives this by rendering every page into a detached element
 * and reading each card's rows. React has no equivalent that is cheap on every
 * keystroke, so the labels are declared here ALONGSIDE the pages and a test
 * (`accountSearchIndex.test.ts`) asserts every card key named here exists in
 * the editing model — the same guarantee, checked at build time rather than
 * render time.
 */

export interface AccountSearchEntry {
  /** The field's label, as the page prints it. */
  label: string;
  page: AccountPageId;
  /** The card that holds it, as the card's head prints it. */
  card: string;
  /** The card's key in the editing model — the result opens this card. */
  cardKey?: string;
}

const entry = (
  page: AccountPageId,
  card: string,
  cardKey: string | undefined,
  labels: string[],
): AccountSearchEntry[] => labels.map((label) => ({ label, page, card, cardKey }));

export const ACCOUNT_SEARCH_INDEX: readonly AccountSearchEntry[] = [
  ...entry('personal', 'Personal information', 'you', [
    'Name',
    'Preferred name',
    'Account email',
    'Primary phone',
    'Secondary phone',
    'Time zone',
  ]),
  ...entry('personal', 'Mailing address', 'addr', ['Personal mailing address']),
  ...entry('personal', 'Billing address', 'billaddr', ['Billing address']),

  ...entry('password', 'Password', undefined, ['Password', 'Last changed']),

  ...entry('gem', 'GEM progression', undefined, [
    'GEM level',
    'Qualifying revenue',
    'Open invoices',
  ]),

  ...entry('business', 'Business details', 'biz', [
    'Legal entity name',
    'Legal entity type',
    'In business since',
    'Business time allocation',
  ]),

  ...entry('public', 'Brand', 'brand', [
    'Logo',
    'Brand name',
    'Tagline',
    'Business description',
  ]),
  ...entry('public', 'Advisor bio', 'pub', [
    'Headshot',
    'Public name',
    'Headline',
    'Short introduction',
    'Full bio',
    'In travel since',
    'Awards + certifications',
  ]),
  ...entry('public', 'Specialties + expertise', 'spec', [
    'Destinations',
    'Client interests',
    'Travel styles',
    'Preferred suppliers',
    'Typical budget range',
  ]),
  ...entry('public', 'Public contact + booking', 'cta', [
    'Public email',
    'Public phone',
    'Public location',
    'Social links',
  ]),

  ...entry('team', 'Team members', undefined, ['Team members', 'Role', 'Status']),
  ...entry('team', 'Roles + permissions', undefined, ['Roles + permissions']),

  ...entry('membership', 'Membership', undefined, [
    'Plan',
    'Membership price',
    'Billing frequency',
    'Next renewal',
  ]),
  ...entry('membership', 'Commission', undefined, ['Commission', 'Plan addendum']),
  ...entry('membership', 'Agreements', undefined, [
    'Service agreement',
    'Associate addendum',
  ]),
  ...entry('membership', 'Industry credentials', undefined, [
    'IATA',
    'ARC',
    'CLIA',
    'IATAN',
    'Seller of Travel',
  ]),

  ...entry('billing', 'Payment method', 'bill', ['Expires', 'Billing address']),
  ...entry('billing', 'Billing history', undefined, ['Billing history']),

  ...entry('documents', 'Business continuity', undefined, [
    'Designation status',
    'Designated contact',
  ]),
  ...entry('documents', 'Tax information', undefined, ['W-9', 'EIN']),
  ...entry('documents', 'Business document library', undefined, [
    'E&O insurance',
    'Seller of Travel registration',
    'Business registration',
  ]),
];

/** The pages this member can reach — the search never opens a page they cannot see. */
export function visibleAccountPages(role: AccountRole): AccountPageId[] {
  return ACCOUNT_PAGE_IDS.filter((id) => {
    if (!accountPageInSprint(id)) return false;
    if (ACCOUNT_OWNER_ONLY.includes(id) && role !== 'Owner') return false;
    if (id === 'team' && !showsTeamPage(role)) return false;
    return true;
  });
}

export interface AccountSearchHit extends AccountSearchEntry {
  path: string;
  pageLabel: string;
}

/** At most eight hits, matched on the label the reader can see. */
export function searchAccountSettings(
  query: string,
  role: AccountRole,
): AccountSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const visible = new Set(visibleAccountPages(role));
  const seen = new Set<string>();
  const hits: AccountSearchHit[] = [];
  for (const e of ACCOUNT_SEARCH_INDEX) {
    if (!visible.has(e.page)) continue;
    if (!e.label.toLowerCase().includes(q)) continue;
    const key = `${e.label}|${e.page}|${e.card}`;
    if (seen.has(key)) continue;
    seen.add(key);
    hits.push({
      ...e,
      path: ACCOUNT_PATHS[e.page],
      pageLabel: ACCOUNT_NAV_LABELS[e.page],
    });
    if (hits.length >= 8) break;
  }
  return hits;
}
